import { Box } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { FONT_MONO } from "../../theme/v2Tokens";

interface MetadataTabProps {
  metadata: Record<string, unknown>;
}

interface Field {
  label: string;
  value: string;
  mono?: boolean;
}

/** Keys that should be formatted as dates. */
const DATE_KEYS = new Set(["startTime", "endTime", "importTimestamp", "createdAt", "updatedAt"]);
/** Keys that hold epoch-ms or epoch-second retention dates. */
const EPOCH_DATE_KEYS = new Set(["retentionDate", "retentionDateHold"]);
/** Keys that should be hidden from display. */
const HIDDEN_KEYS = new Set(["archivePath", "fileName", "transcriptPath", "redactedTranscriptPath"]);

function formatDateValue(value: unknown): string {
  if (value == null || value === "") return "\u2014";
  try {
    const d = new Date(value as string | number);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
  } catch {
    return String(value);
  }
}

function formatDuration(ms: unknown): string {
  if (typeof ms !== "number") return String(ms);
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
}

function humanizeKey(key: string): string {
  // Convert camelCase to Title Case
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

function formatBooleanValue(value: boolean, t?: TFunction): string {
  if (t) return value ? t("common:yes") : t("common:no");
  return value ? "Yes" : "No";
}

function formatFieldValue(key: string, value: unknown, t?: TFunction): string {
  if (value == null || value === "") return "\u2014";

  if (DATE_KEYS.has(key)) return formatDateValue(value);
  if (EPOCH_DATE_KEYS.has(key)) return formatDateValue(value);
  if (key === "durationMs") return formatDuration(value);

  if (typeof value === "boolean") return formatBooleanValue(value, t);
  if (Array.isArray(value)) return value.length > 0 ? value.join(", ") : "\u2014";
  if (typeof value === "object") return JSON.stringify(value);

  return String(value);
}

const MONO_KEYS = new Set([
  "interactionId",
  "masterInteractionId",
  "sourceId",
  "callId",
  "customerId",
  "siteId",
  "dnis",
  "phoneNumber",
  "extension",
  "codec",
  "segmentIds",
]);

export default function MetadataTab({ metadata }: MetadataTabProps) {
  const { t } = useTranslation("common");
  const { crFields, sourceFields } = useMemo(() => {
    const cr: Field[] = [];
    const src: Field[] = [];

    Object.entries(metadata).forEach(([key, value]) => {
      if (HIDDEN_KEYS.has(key)) return;
      if (value == null || value === "") return;

      const displayValue = formatFieldValue(key, value, t);
      if (displayValue === "\u2014") return;

      if (key.startsWith("src_")) {
        const cleanKey = key.replace(/^src_/, "");
        src.push({
          label: humanizeKey(cleanKey),
          value: displayValue,
          mono: false,
        });
      } else {
        cr.push({
          label: humanizeKey(key),
          value: displayValue,
          mono: MONO_KEYS.has(key),
        });
      }
    });

    return { crFields: cr, sourceFields: src };
  }, [metadata, t]);

  if (crFields.length === 0 && sourceFields.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 120,
          color: "var(--text-tertiary)",
          fontSize: "0.8125rem",
        }}
      >
        {t("noMetadataAvailable")}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      {crFields.length > 0 && (
        <CollapsibleSection title={t("continuityReplay")} defaultOpen>
          <FieldGrid fields={crFields} />
        </CollapsibleSection>
      )}
      {sourceFields.length > 0 && (
        <CollapsibleSection title={t("sourceMetadata")} defaultOpen>
          <FieldGrid fields={sourceFields} />
        </CollapsibleSection>
      )}
    </Box>
  );
}

function CollapsibleSection({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Box>
      <Box
        component="button"
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          m: 0,
          mb: open ? 1 : 0,
          p: 0,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          fontSize: "0.6875rem",
          fontWeight: 700,
          color: "var(--text-tertiary)",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          background: "none",
          border: "none",
          cursor: "pointer",
          fontFamily: "inherit",
          "&:hover": { color: "var(--text-secondary)" },
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          sx={{
            width: 14,
            height: 14,
            transition: "transform 150ms ease",
            transform: open ? "rotate(90deg)" : "rotate(0deg)",
          }}
        >
          <polyline points="9 18 15 12 9 6" />
        </Box>
        {title}
      </Box>
      {open && children}
    </Box>
  );
}

function FieldGrid({ fields }: { fields: Field[] }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 1,
        alignContent: "start",
      }}
    >
      {fields.map((field) => (
        <Box
          key={field.label}
          sx={{
            p: 1.25,
            backgroundColor: "var(--bg-raised)",
            border: "1px solid var(--border-subtle)",
            borderRadius: "8px",
            minWidth: 0,
          }}
        >
          <Box
            component="div"
            sx={{
              fontSize: "0.6875rem",
              color: "var(--text-tertiary)",
              mb: 0.25,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              fontWeight: 500,
            }}
          >
            {field.label}
          </Box>
          <Box
            component="div"
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 500,
              color: "var(--text-primary)",
              fontFamily: field.mono ? FONT_MONO : "inherit",
              wordBreak: "break-all",
            }}
          >
            {field.value}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
